"use client";

import { useId, useState } from "react";

interface AccordionItem {
  question: string;
  answer: string;
}

/**
 * Restyled FAQ accordion (brief 5.9): square and borderless, gold
 * hairline separators, Fraunces heading-4 questions, a plus glyph that
 * rotates 45 degrees to an X on open, and the SI-11 panel height
 * animation via CSS grid-template-rows 0fr to 1fr. All answers stay in
 * the DOM (collapsed panels are height-clamped, not unmounted) so the
 * copy remains crawlable; the page also emits FAQPage schema.
 *
 * Disclosure semantics: each question is an h3 wrapping the toggle
 * button (heading structure for answer engines and AT), with
 * aria-controls pointing at a labelled region panel.
 */
export function FaqAccordion({ items }: { items: AccordionItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const baseId = useId();

  return (
    <div className="border-t silk-hairline">
      {items.map((item, index) => {
        const isOpen = openIndex === index;
        const buttonId = `${baseId}-faq-${index}-button`;
        const panelId = `${baseId}-faq-${index}-panel`;
        return (
          <div key={index} className="border-b silk-hairline">
            <h3>
              <button
                type="button"
                id={buttonId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenIndex(isOpen ? null : index)}
                className="group flex w-full items-center justify-between gap-6 py-6 text-left"
              >
                <span className="silk-heading-4 text-ink transition-colors group-hover:text-gold-deep">
                  {item.question}
                </span>
                <span
                  aria-hidden="true"
                  className={`relative flex h-4 w-4 flex-shrink-0 items-center justify-center transition-transform duration-300 ease-[cubic-bezier(0.65,0,0.35,1)] ${
                    isOpen ? "rotate-45" : ""
                  }`}
                >
                  <span className="absolute h-px w-full bg-gold-deep" />
                  <span className="absolute h-full w-px bg-gold-deep" />
                </span>
              </button>
            </h3>

            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              className="grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.65,0,0.35,1)]"
              style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
            >
              <div className="overflow-hidden">
                <p className="silk-body max-w-[45rem] pb-6 text-body">
                  {item.answer}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
